import { Component } from '@theme/component';

/**
 * Bo+Tee Tabbed Banner — builds tabs from panel data attributes (theme block
 * settings only hydrate inside {% content_for 'blocks' %}), then switches
 * panels + CTA on activation.
 *
 * @extends {Component}
 */
class BtTabbedBanner extends Component {
  /** @type {HTMLElement | null} */
  #tablist = null;

  connectedCallback() {
    super.connectedCallback();
    this.#tablist = this.querySelector('[role="tablist"]');
    this.#hydrateTabsFromPanels();
    this.addEventListener('click', this.#onClick);
    this.#tablist?.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.#tablist?.removeEventListener('keydown', this.#onKeydown);
  }

  /**
   * Build tab buttons from each panel's data-tab-label / data-tab-link.
   */
  #hydrateTabsFromPanels() {
    if (!this.#tablist) return;

    this.#tablist.replaceChildren();

    const panels = this.querySelectorAll('[role="tabpanel"]');
    const linkLabel = this.dataset.linkLabel || 'view collection';
    let firstButton = null;
    let index = 0;

    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;
      const label = panel.dataset.tabLabel?.trim();
      const tabId = panel.dataset.tabId;
      if (!label || !tabId) continue;

      const isActive = panel.classList.contains('is-active') || (!firstButton && index === 0);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `bt-tabbed-banner__tab text-h3${isActive ? ' is-active' : ''}`;
      button.id = `BtTabbedBanner-tab-${tabId}`;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.setAttribute('aria-controls', panel.id);
      button.tabIndex = isActive ? 0 : -1;
      button.dataset.tabId = tabId;
      button.dataset.link = panel.dataset.tabLink || '';
      button.dataset.linkLabel = linkLabel;
      button.textContent = label;
      this.#tablist.appendChild(button);

      if (isActive) {
        firstButton = button;
        panel.classList.add('is-active');
        panel.hidden = false;
      } else {
        panel.classList.remove('is-active');
        panel.hidden = true;
      }
      index += 1;
    }

    if (firstButton) {
      this.#updateCta(firstButton);
      this.#applyOverlayColor(this.#activePanel());
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const tab = target.closest('[role="tab"]');
    if (!(tab instanceof HTMLElement) || !this.contains(tab)) return;
    this.#activate(tab, { focus: false });
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const tabs = this.#tabs();
    if (!tabs.length) return;

    const current = document.activeElement;
    if (!(current instanceof HTMLElement) || current.getAttribute('role') !== 'tab') return;

    const index = tabs.indexOf(current);
    if (index < 0) return;

    let nextIndex = -1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(tabs[nextIndex], { focus: true });
  };

  /**
   * @returns {HTMLElement[]}
   */
  #tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]')).filter(
      (el) => el instanceof HTMLElement
    );
  }

  /**
   * @param {HTMLElement} tab
   * @param {{ focus?: boolean }} [options]
   */
  #activate(tab, options = {}) {
    const tabId = tab.dataset.tabId;
    if (!tabId) return;

    for (const other of this.#tabs()) {
      const selected = other === tab;
      other.classList.toggle('is-active', selected);
      other.setAttribute('aria-selected', selected ? 'true' : 'false');
      other.tabIndex = selected ? 0 : -1;
    }

    for (const panel of this.querySelectorAll('[role="tabpanel"]')) {
      if (!(panel instanceof HTMLElement)) continue;
      const active = panel.dataset.tabId === tabId;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    }

    this.#updateCta(tab);
    this.#applyOverlayColor(this.#activePanel());

    if (options.focus) {
      tab.focus();
    }
  }

  /**
   * @param {HTMLElement} tab
   */
  #updateCta(tab) {
    const cta = this.querySelector('[data-bt-tabbed-banner-cta]');
    if (!(cta instanceof HTMLElement)) return;

    const link = tab.dataset.link?.trim() || '';
    const label = tab.dataset.linkLabel?.trim() || this.dataset.linkLabel || '';

    if (label) {
      cta.textContent = label;
    }

    if (cta instanceof HTMLAnchorElement) {
      if (link) {
        cta.href = link;
        cta.hidden = false;
        cta.removeAttribute('aria-hidden');
      } else {
        cta.hidden = true;
        cta.removeAttribute('href');
      }
      return;
    }

    if (link && label) {
      const anchor = document.createElement('a');
      anchor.href = link;
      anchor.className = cta.className;
      anchor.dataset.btTabbedBannerCta = '';
      anchor.textContent = label;
      cta.replaceWith(anchor);
    } else {
      cta.hidden = true;
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #activePanel() {
    for (const panel of this.querySelectorAll('[role="tabpanel"]')) {
      if (panel instanceof HTMLElement && panel.classList.contains('is-active')) {
        return panel;
      }
    }
    return null;
  }

  /**
   * Overlay copy (eyebrow, tabs, CTA) uses the active tab's colour.
   *
   * @param {HTMLElement | null} panel
   */
  #applyOverlayColor(panel) {
    const color = panel?.dataset.tabColor?.trim() || '';
    const rgb = (panel?.dataset.tabColorRgb || '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim();

    if (color && rgb) {
      this.style.setProperty('--bt-tabbed-banner-text', color);
      this.style.setProperty(
        '--bt-tabbed-banner-text-muted',
        `rgb(${rgb} / var(--bt-tabbed-banner-muted-alpha, 0.5))`
      );
      return;
    }

    this.style.removeProperty('--bt-tabbed-banner-text');
    this.style.removeProperty('--bt-tabbed-banner-text-muted');
  }
}

if (!customElements.get('bt-tabbed-banner')) {
  customElements.define('bt-tabbed-banner', BtTabbedBanner);
}
